// The addresses of the season chronicle (chronicle.md §8, §9.4, R1181) and
// of the sea page (§9.5, R3300).
//
// Both are query parameters of the one game page (`/`), and not new Vite
// entries. A new entry needs a clean-URL rule (R866) and a line in the
// nginx and deploy templates; a mode of the page needs neither.
//
//   ?chronicle=<publicId>.<world>.<season>
//       the public reader: a shared closed volume, read with no session.
//   ?timelapse=<world>.<season>
//       the sea page: the season timelapse of a closed season, read with
//       no session and no share (R3300).
//   ?world=<world>&replay=<kind>.<t0>.<t1>.<cell>.<fleet>-<fleet>-…
//       the replay link of a battle line: the entry joins the world and
//       asks for the film after the first Empire frame.
//   ?tab=record&page=chronicle&book=<world>[&film=gone|silent]
//       the way back from a replay link: the hub opens the book on the
//       live volume of that world, with the notice of a film that never
//       came (R1684).
//
// A public id is a UUID and holds no dot, so the dot is a safe separator.
// Every other part is a decimal integer. The two readers refuse a value
// they cannot read whole, because a half-read address must never fetch a
// volume or a film nobody asked for.

import type { ChronicleReplay } from '../../../gen/chronicle';
import type { ReplayRef } from '../../../gen/proto/aquaphobia';
import { PAGE_CHRONICLE, PAGE_PARAM } from '../panel';

/** The query key of the public reader. */
const READER_KEY = 'chronicle';
/** The query key of the sea page (R3300). The meta server writes the same
 *  word into the link of a season result post (`seaParam` of
 *  `server/internal/metasrv/httpapi/discord.go`). */
export const SEA_KEY = 'timelapse';
/** The query key of the replay link. */
const REPLAY_KEY = 'replay';

/** The query key of the world whose live volume the book opens on. */
export const BOOK_KEY = 'book';
/** The query key of the outcome of a replay link that played no film. */
export const FILM_KEY = 'film';

/** Why a replay link played no film: the server refused the reference
 *  (`replay_gone`), or no answer came in `replay_link_wait_ms`. */
export type FilmMiss = 'gone' | 'silent';

/** One shared volume, as the public reader's address names it. */
export interface ReaderAddress {
  publicId: string;
  world: number;
  season: number;
}

/** A whole number of 0 or more, or -1 for anything else. */
function whole(raw: string): number {
  return /^[0-9]{1,15}$/.test(raw) ? Number(raw) : -1;
}

/** A positive whole number, or 0 for anything else. */
function positive(raw: string): number {
  return Math.max(0, whole(raw));
}

/** The page the two addresses go to: this origin and this path, with no
 *  query. The hub and the game are one page (`fast/entry.ts`). */
export function pageBase(
  loc: { origin: string; pathname: string } | undefined = globalThis.location,
): string {
  // A test with no DOM has no location: the root of this origin.
  return loc === undefined ? '/' : `${loc.origin}${loc.pathname}`;
}

/** The address of the public reader of one volume. */
export function readerHref(base: string, a: ReaderAddress): string {
  return `${base}?${READER_KEY}=${encodeURIComponent(`${a.publicId}.${a.world}.${a.season}`)}`;
}

/** Reads the public reader's address, or undefined when the page names
 *  no volume or names one that cannot be read. */
export function readReaderAddress(params: URLSearchParams): ReaderAddress | undefined {
  const raw = params.get(READER_KEY);
  if (raw === null) return undefined;
  const parts = raw.split('.');
  if (parts.length !== 3) return undefined;
  const [publicId, w, s] = parts;
  const world = positive(w);
  const season = positive(s);
  if (!/^[0-9A-Za-z-]{1,64}$/.test(publicId) || world === 0 || season === 0) return undefined;
  return { publicId, world, season };
}

/** One closed season, as the sea page's address names it (R3300). */
export interface SeaAddress {
  world: number;
  season: number;
}

/** The address of the sea page of one closed season. */
export function seaHref(base: string, a: SeaAddress): string {
  return `${base}?${SEA_KEY}=${a.world}.${a.season}`;
}

/** Reads the sea page's address, or undefined when the page names no
 *  season or names one that cannot be read whole. */
export function readSeaAddress(params: URLSearchParams): SeaAddress | undefined {
  const raw = params.get(SEA_KEY);
  if (raw === null) return undefined;
  const parts = raw.split('.');
  if (parts.length !== 2) return undefined;
  const world = positive(parts[0]);
  const season = positive(parts[1]);
  return world === 0 || season === 0 ? undefined : { world, season };
}

/** One replay link: the world to join and the reference to ask for. */
export interface ReplayLink {
  world: number;
  ref: ReplayRef;
}

/** The query value of one reference, or '' when it names no fleet. The
 *  server refuses a reference with no fleet (`hub.fetchReplay`). */
export function replayValue(r: ChronicleReplay): string {
  const fleets = (r.fleets ?? []).filter((f) => Number.isInteger(f) && f >= 0);
  if (fleets.length === 0) return '';
  const n = (v: number | undefined): number => (Number.isInteger(v) && (v ?? 0) >= 0 ? v ?? 0 : 0);
  return [n(r.kind), n(r.t0), n(r.t1), n(r.cell), fleets.join('-')].join('.');
}

/** The address of the replay link of one battle fact in one world, or ''
 *  when the fact carries no reference the server can play. */
export function replayHref(base: string, world: number, r: ChronicleReplay | undefined): string {
  if (!r || world <= 0) return '';
  const v = replayValue(r);
  return v === '' ? '' : `${base}?world=${world}&${REPLAY_KEY}=${v}`;
}

/** Reads the replay link off a page address, or undefined when there is
 *  none or it cannot be read whole. */
export function readReplayLink(params: URLSearchParams): ReplayLink | undefined {
  const raw = params.get(REPLAY_KEY);
  const world = positive(params.get('world') ?? '');
  if (raw === null || world === 0) return undefined;
  const parts = raw.split('.');
  if (parts.length !== 5) return undefined;
  const [k, t0, t1, cell] = parts.slice(0, 4).map(whole);
  const fleets = parts[4].split('-').map(whole);
  if (k < 0 || k > 255 || t0 < 0 || t1 < 0 || cell < 0) return undefined;
  if (fleets.length === 0 || fleets.some((f) => f < 0)) return undefined;
  return {
    world,
    ref: { kind: k as ReplayRef['kind'], fleets, t0Ms: t0, t1Ms: t1, cell },
  };
}

/** The way back from a replay link to the book it came from (R1684).
 *  `hub` is the hub address of the page (`fast/actions.ts` `hubAddress`
 *  with the RECORD section), so every other parameter stays. */
export function bookAddress(hub: string, world: number, miss?: FilmMiss): string {
  const url = new URL(hub);
  url.searchParams.set(PAGE_PARAM, PAGE_CHRONICLE);
  url.searchParams.set(BOOK_KEY, String(world));
  if (miss !== undefined) url.searchParams.set(FILM_KEY, miss);
  return url.toString();
}

/** Reads the way back off a page address: the world of the volume, or 0,
 *  and the outcome of the film, or undefined when a film played. */
export function readBookReturn(params: URLSearchParams): { world: number; miss?: FilmMiss } {
  const film = params.get(FILM_KEY);
  const miss = film === 'gone' || film === 'silent' ? film : undefined;
  return { world: positive(params.get(BOOK_KEY) ?? ''), miss };
}

/** Takes the replay parameter back off the address bar. A reload must not
 *  ask for the film again, and a later join into another world keeps the
 *  address (`rememberWorld`) and must not carry it there. */
export function forgetReplayLink(): void {
  const url = new URL(location.href);
  if (!url.searchParams.has(REPLAY_KEY)) return;
  url.searchParams.delete(REPLAY_KEY);
  history.replaceState(null, '', url.toString());
}
